import React, { FunctionComponent } from 'react';
import { useUserStore } from '@/zustand';
import RoomSurfaces from '@r3f/components/RoomSurfaces/RoomSurfaces';

/**
 * The room the world-tracking preview drops content into.
 *
 * World tracking has no target to build a room around and no tracked plane to sit on, so this is
 * simply somewhere for the content to be: a floor at y=0, where the instant tracker's anchor would
 * have landed.
 *
 * Floor and sky only, no wall — the same choice the image preview makes for a target lying flat.
 * Orbiting here goes all the way round, and a wall you can pass behind is set dressing that spends
 * most of its time in the way.
 *
 * Mounted only while a WORLD-tracked scene is active in the desktop preview — the experience stays in
 * AR mode throughout, so this comes and goes as scenes change. See PREVIEW_OVERVIEW.md §10.
 */

/**
 * The preview runs in AR mode, where a scene unit IS a metre — unlike the web embed's non-AR mode,
 * which renders everything at twice that. Get this wrong and the room is the right shape at half or
 * double the believable size.
 */
const UNITS_PER_METRE = 1;

/** Large enough that its edges stay out of frame while orbiting, since you can back away in any direction. */
const FLOOR_SIZE_M = 16;

/**
 * Twice the real-world board size.
 *
 * The image preview scales its room from the target's printed height, so boards there should measure
 * what they claim. Nothing here has a printed reference, and across a 16m floor real 60mm boards read
 * as fine stripes from the distance the camera settles at.
 */
const FLOOR_TILE_SCALE = 2;

/**
 * The floor needs no colour correction at all, which is worth stating because it briefly did.
 *
 * The room is lit by the scene's environment map, and JUTE derives that from the camera feed. While the
 * shell left the feed unpainted on a world scene the floor came out far too dark, and tinting or
 * self-lighting it to compensate only traded that for a washed-out floor once the feed held a bright
 * frame. The shell now paints a neutral, so the plank texture renders as drawn.
 */

const PreviewRoom: FunctionComponent = () => {
	// Set by the host, never from inside the experience — see `Jute.setPreviewRoomDark`. The boards are
	// redrawn dark rather than tinted, so nothing here changes but the texture that comes back.
	const dark = useUserStore((state) => state.isPreviewRoomDark as boolean);

	return (
		<group name="preview-room">
			<RoomSurfaces
				unitsPerMetre={UNITS_PER_METRE}
				floorSizeM={FLOOR_SIZE_M}
				floorTileScale={FLOOR_TILE_SCALE}
				dark={dark}
			/>
		</group>
	);
};

PreviewRoom.displayName = 'PreviewRoom';
export default PreviewRoom;
